import { useState } from 'react'

import { EditButton, IllustratedRow, InfoRow, SectionCard } from '@/components/Chat/ConfirmInfoCard'
import { IllustrationName } from '@/components/icons/Illustration'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { COVERAGE_TYPE_LABELS } from '@/models/insuranceCoverage'
import { LOSS_RUNS_MORE } from '@/routes/Private/ConfirmInfo/LossRunsSummary'
import {
  BUSINESS_DETAILS,
  COVERAGE_NEEDS,
  DRIVING_DISTANCE,
  FREIGHT_TYPES,
  OWNER_INFO,
} from '../ConfirmInfoCards/confirmInfoData'

const BUSINESS_DETAIL = Object.fromEntries(BUSINESS_DETAILS.map((row) => [row.label, row.value]))
const OWNER = Object.fromEntries(OWNER_INFO.map((row) => [row.label, row.value]))

// BusinessSummary's rows, in its order, filled from the prototype's fixtures.
const BUSINESS_ROWS = [
  { label: 'Company name', value: BUSINESS_DETAIL['Company name'] },
  { label: 'DOT number', value: BUSINESS_DETAIL['DOT number'] },
  { label: 'Business type', value: BUSINESS_DETAIL['Business type'] },
  { label: 'Address', value: BUSINESS_DETAIL['Address'] },
  { label: 'Years in business', value: BUSINESS_DETAIL['Years in business'] },
  { label: 'ELD provider', value: BUSINESS_DETAIL['ELD provider'] },
  { label: 'Business owner', value: OWNER['Owner name'] },
  { label: 'Owner email', value: OWNER['Email'] },
  { label: 'Owner phone', value: OWNER['Phone number'] },
  { label: 'Owner date of birth', value: OWNER['Date of birth'] },
  { label: 'Owner marital status', value: OWNER['Marital status'] },
  { label: 'Travel distance', value: DRIVING_DISTANCE.join(', ') },
  { label: 'Freight', value: FREIGHT_TYPES.map((item) => `${item.name} (${item.percentage}%)`).join(', ') },
]

// Copied from REVIEW_ATTESTATION_TEXT in cailabs/insure/services/insurance/review_attestation.py.
const REVIEW_ATTESTATION_TEXT =
  'I certify that I have reviewed the application and all prefilled information. All information is true, complete, and accurate. I have corrected any inaccurate or incomplete information. I have disclosed all material facts requested, and I understand that false, incomplete, misleading, or omitted material information may result in rescission, cancellation, denial of claims, premium adjustment, or other remedies permitted by applicable law and the policy. This certification may not be completed by an automated tool, software agent, or AI system.'

/** The /insurance/confirm-info review's sections in a modal; any edit needs a fresh certification before it's sent. */
export function ConfirmInfoModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [edited, setEdited] = useState<ReadonlySet<string>>(new Set())
  const [certified, setCertified] = useState(false)

  function edit(section: string) {
    setEdited((current) => new Set(current).add(section))
    setCertified(false)
  }

  function close(nextOpen: boolean) {
    if (!nextOpen) {
      setEdited(new Set())
      setCertified(false)
    }
    onOpenChange(nextOpen)
  }

  const action = (section: string, title: string) =>
    edited.has(section) ? (
      <Badge variant="secondary">Updated</Badge>
    ) : (
      <EditButton onClick={() => edit(section)} label={`Edit ${title}`} />
    )

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="lg" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Update insurance application</DialogTitle>
          <DialogDescription>
            Your providers priced your coverage on this information. Any change has to be certified again
            before it&apos;s sent to them.
          </DialogDescription>
        </DialogHeader>

        {/* ConfirmInfoForm's sections, in its order, with its summaries in place of the live queries. */}
        <div className="flex flex-col gap-4">
          <SectionCard title="Business info" action={action('business', 'Business info')}>
            {BUSINESS_ROWS.map((row) => (
              <InfoRow key={row.label} label={row.label} value={row.value} />
            ))}
          </SectionCard>

          <SectionCard title="Coverage" action={action('coverage', 'Coverage')}>
            <InfoRow label="Coverage starts" value={COVERAGE_NEEDS.startsOn} />
            <div className="flex flex-col gap-0.5 md:flex-row md:gap-4">
              <span className="text-ink-400 md:w-44 md:shrink-0">Requested coverage(s)</span>
              <div className="flex min-w-0 flex-col gap-0.5">
                {COVERAGE_NEEDS.coverages.map((coverage) => (
                  <span key={coverage} className="text-foreground">
                    {COVERAGE_TYPE_LABELS[coverage]}
                  </span>
                ))}
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Equipment and drivers" action={action('equipment', 'Equipment and drivers')}>
            <div className="flex flex-col gap-3">
              <IllustratedRow illustration={IllustrationName.DRIVER} label="4 drivers" />
              <IllustratedRow illustration={IllustrationName.TRUCK} label="3 trucks" />
              <IllustratedRow illustration={IllustrationName.TRAILER} label="2 trailers" />
            </div>
          </SectionCard>

          <SectionCard title="Loss run reports" action={action('lossRuns', 'Loss run reports')}>
            <IllustratedRow illustration={IllustrationName.LOSS_RUN} label="7 claims on file" />
            <p className="text-muted-foreground text-xs">{LOSS_RUNS_MORE}</p>
          </SectionCard>
        </div>

        {/* Same markup as AttestationConsentComponent, which fetches its wording from the API. */}
        <div className="border-primary/20 bg-muted/50 flex items-start gap-3 rounded-lg border p-3">
          <Checkbox
            id="checkout-review-attestation"
            checked={certified}
            onCheckedChange={(checked) => setCertified(!!checked)}
            className="mt-0.5 size-5"
          />
          <label
            htmlFor="checkout-review-attestation"
            className="block cursor-pointer text-sm whitespace-pre-wrap"
          >
            {REVIEW_ATTESTATION_TEXT}
          </label>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="link" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button type="button" disabled={edited.size === 0 || !certified} onClick={() => close(false)}>
            Submit changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
